import React, { useEffect, useRef } from 'react';

export default function NovaAIBackground() {
  const videoRef = useRef(null);
  const isSeekingRef = useRef(false);
  const pendingSeekRef = useRef(null);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    video.pause();
    const handleLoaded = () => {
      try {
        video.currentTime = 0.01;
      } catch (err) {
        // ignore seek error on unprimed media
      }
    };

    if (video.readyState >= 1) {
      handleLoaded();
    } else {
      video.addEventListener('loadedmetadata', handleLoaded, { once: true });
    }

    const handleSeeked = () => {
      isSeekingRef.current = false;
      if (pendingSeekRef.current !== null) {
        const nextTime = pendingSeekRef.current;
        pendingSeekRef.current = null;
        if (videoRef.current) {
          isSeekingRef.current = true;
          videoRef.current.currentTime = nextTime;
        }
      }
    };
    video.addEventListener('seeked', handleSeeked);

    const performSeek = (targetTime) => {
      if (!video.duration || isNaN(video.duration)) return;
      const clamped = Math.max(0, Math.min(video.duration - 0.05, targetTime));
      if (Math.abs(video.currentTime - clamped) < 0.02) return;

      if (!isSeekingRef.current) {
        isSeekingRef.current = true;
        video.currentTime = clamped;
      } else {
        pendingSeekRef.current = clamped;
      }
    };

    let animationFrameId;
    let targetProgress = 0;
    let smoothedProgress = 0;

    // Scroll mapping specifically for second page onwards (#cinematic-portfolio: About -> Footer)
    const handleScroll = () => {
      const container = document.getElementById('cinematic-portfolio');
      if (container) {
        const rect = container.getBoundingClientRect();
        // Distance from top of container to top of viewport
        const scrollDistance = -rect.top;
        // Total scrollable distance across About -> Footer
        const totalScrollable = Math.max(1, container.offsetHeight - window.innerHeight);
        targetProgress = Math.min(1, Math.max(0, scrollDistance / totalScrollable));
      }
    };

    const renderLoop = () => {
      smoothedProgress += (targetProgress - smoothedProgress) * 0.15;
      if (video && video.duration && !isNaN(video.duration)) {
        const targetTime = smoothedProgress * Math.max(0, video.duration - 0.05);
        performSeek(targetTime);
      }
      animationFrameId = requestAnimationFrame(renderLoop);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleScroll, { passive: true });

    let resizeObserver = null;
    const container = document.getElementById('cinematic-portfolio');
    if (container && typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(() => {
        handleScroll();
      });
      resizeObserver.observe(container);
    }

    handleScroll();
    animationFrameId = requestAnimationFrame(renderLoop);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
      cancelAnimationFrame(animationFrameId);
      if (video) {
        video.removeEventListener('seeked', handleSeeked);
      }
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, []);

  return (
    <div
      className="nova-cinematic-background absolute inset-0 w-full h-full pointer-events-none overflow-hidden"
      aria-hidden="true"
    >
      {/* 1080p native video */}
      <video
        ref={videoRef}
        src="/nova-hero.mp4"
        muted
        playsInline
        preload="auto"
        className="absolute inset-0 h-full w-full object-cover"
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          opacity: 1,
        }}
      />

      {/* Light Glassy White Background Overlay */}
      <div
        className="absolute inset-0 w-full h-full pointer-events-none"
        style={{
          backgroundColor: 'rgba(255, 255, 255, 0.30)',
          backdropFilter: 'blur(8px)',
          WebkitBackdropFilter: 'blur(8px)',
        }}
      />
    </div>
  );
}
